/* eslint-disable @typescript-eslint/no-unused-vars */
import { ErrorBoundary, wrapCreateBrowserRouterV7 } from '@sentry/react';
import {
  Navigate,
  Outlet,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router';

import {
  ActivityOverview,
  AIVisibility,
  AttributionOverview,
  AudienceDetail,
  AudienceDetailEdit,
  AudiencesList,
  BenchmarksOverview,
  CampaignDetail,
  CampaignOverview,
  ChannelDetail,
  ChannelMappingRules,
  ChannelOverview,
  CompanyDetail,
  CompanySettings,
  ConfigureOrg,
  CreateAIAudience,
  CreateCustomAudience,
  CreateRuleBasedAudience,
  Dashboard,
  DiscoveryOrgs,
  EventLog,
  ForgotPassword,
  HealthCenter,
  Home,
  ImportCRMAudience,
  InstanceForgotPassword,
  InstanceSignIn,
  InstanceSignInEmail,
  InstanceSignInPassword,
  IntegrationDetail,
  IntegrationsList,
  LoginAs,
  OAuthConsent,
  OpportunitiesOverview,
  OpportunityDetail,
  PaidSearchOverview,
  PixelCompare,
  PixelCreate,
  PixelDetail,
  PixelEdit,
  PixelsOverview,
  Profile,
  SignIn,
  SignInEmail,
  SignInPassword,
  SignInSsoEmail,
  SignUp,
  SignUpEmail,
  UserDetail,
  UserInvite,
  UsersList,
  VendorDetail,
  VendorOverview,
  VendorSpend,
  VerifyIntegration,
  WebTraffic,
} from '@/app';
import Error500 from '@/components/errors/Error500';
import { InstanceNavigate } from '@/components/routing';
import { FEATURE_FLAG } from '@/constants/featureFlags';

import AppInstanceRedirect from './AppInstanceRedirect';
import AuthenticatedRoute from './AuthenticatedRoute';
import DefaultAppRedirect from './DefaultAppRedirect';
import FeatureFlagRoute from './FeatureFlagRoute';
import PageNotFound from './PageNotFound';
import PaidRoute from './PaidRoute';
import { ROUTES } from './routes';
import UserRoleRoute from './UserRoute';

const sentryCreateBrowserRouter = wrapCreateBrowserRouterV7(createBrowserRouter);

const getRouter = () => {
  return sentryCreateBrowserRouter(
    createRoutesFromElements(
      <Route
        element={
          <ErrorBoundary fallback={<Error500 />}>
            <Outlet />
          </ErrorBoundary>
        }
      >
        <Route index element={<Navigate to={ROUTES.app.path} replace />} />

        {/* SIGN-IN */}
        <Route path={ROUTES.signIn.path} element={<SignIn />} />
        <Route path={ROUTES.signInInstPasswordForgot.path} element={<InstanceForgotPassword />} />
        <Route path={ROUTES.signInInstPassword.path} element={<InstanceSignInPassword />} />
        <Route path={ROUTES.signInInstEmail.path} element={<InstanceSignInEmail />} />
        <Route path={ROUTES.signInInst.path} element={<InstanceSignIn />} />
        <Route path={ROUTES.signInEmail.path} element={<SignInEmail />} />
        <Route path={ROUTES.signInSso.path} element={<SignInSsoEmail />} />
        <Route path={ROUTES.signInPassword.path} element={<SignInPassword />} />
        <Route path={ROUTES.signInPasswordForgot.path} element={<ForgotPassword />} />
        <Route path={ROUTES.signInDiscovery.path} element={<DiscoveryOrgs />} />

        {/* SIGN UP */}
        <Route path={ROUTES.signUp.path} element={<SignUp />} />
        <Route path={ROUTES.signUpEmail.path} element={<SignUpEmail />} />
        <Route path={ROUTES.configureOrg.path} element={<ConfigureOrg />} />

        {/* OAUTH */}
        <Route
          path={ROUTES.oauth.path}
          element={
            <AuthenticatedRoute>
              <OAuthConsent />
            </AuthenticatedRoute>
          }
        />

        <Route
          path={ROUTES.app.path}
          element={
            <AuthenticatedRoute>
              <AppInstanceRedirect />
            </AuthenticatedRoute>
          }
        >
          <Route path="*" element={<PageNotFound />} />

          <Route path={ROUTES.instance.path} element={<Home />}>
            <Route index element={<DefaultAppRedirect />} />

            <Route path={ROUTES.dashboard.path} element={<Dashboard />} />

            <Route element={<FeatureFlagRoute flag={FEATURE_FLAG.aiVisibility} />}>
              <Route
                path={ROUTES.aiVisibility.path}
                element={<InstanceNavigate to={ROUTES.aiVisibilityOverview.path} replace />}
              />
              <Route path={ROUTES.aiVisibilityOverview.path} element={<AIVisibility />} />
              <Route path={ROUTES.aiVisibilityPrompts.path} element={<AIVisibility />} />
              <Route path={ROUTES.aiVisibilityRecommendations.path} element={<AIVisibility />} />
              <Route
                path={ROUTES.aiVisibilityContent.path}
                element={<InstanceNavigate to={ROUTES.aiVisibilityOverview.path} replace />}
              />
              <Route path={ROUTES.aiVisibilityCompetitors.path} element={<AIVisibility />} />
              <Route element={<UserRoleRoute role="admin" />}>
                <Route path={ROUTES.aiVisibilitySettings.path} element={<AIVisibility />} />
              </Route>
            </Route>

            <Route path={ROUTES.profile.path}>
              <Route index element={<Profile />} />
              <Route element={<UserRoleRoute role="c99Superuser" />}>
                <Route path={ROUTES.loginAs.path} element={<LoginAs />} />
              </Route>
            </Route>

            <Route path={ROUTES.webTraffic.path} element={<WebTraffic />} />
            <Route path={ROUTES.webTrafficBenchmarks.path} element={<BenchmarksOverview />} />
            <Route path={ROUTES.webTrafficActivity.path} element={<ActivityOverview />} />
            <Route path={ROUTES.companyDetail.path} element={<CompanyDetail />} />
            <Route path={ROUTES.attribution.path} element={<AttributionOverview />} />

            <Route element={<UserRoleRoute role="admin" />}>
              {/* SETTINGS */}
              <Route path={ROUTES.users.path}>
                <Route index element={<UsersList />} />
                <Route path={ROUTES.userInvite.path} element={<UserInvite />} />
                <Route path={ROUTES.userDetail.path} element={<UserDetail />} />
              </Route>

              <Route
                path={ROUTES.companySettings.path}
                element={<InstanceNavigate to={ROUTES.companySettingsGeneral.path} replace />}
              />
              <Route path={ROUTES.companySettingsGeneral.path} element={<CompanySettings />} />
              <Route path={ROUTES.companySettingsImpactScore.path} element={<CompanySettings />} />
              <Route path={ROUTES.eventLog.path} element={<EventLog />} />
            </Route>
            <Route path={ROUTES.healthCenter.path} element={<HealthCenter />} />

            <Route element={<PaidRoute />}>
              {/* CHANNELS */}
              <Route path={ROUTES.channels.path} element={<ChannelOverview />} />
              <Route path={ROUTES.channelDetail.path} element={<ChannelDetail />} />

              {/* VENDORS */}
              <Route path={ROUTES.vendors.path} element={<VendorOverview />} />
              <Route path={ROUTES.vendorDetail.path} element={<VendorDetail />} />

              {/* CAMPAIGNS */}
              <Route path={ROUTES.campaigns.path} element={<CampaignOverview />} />
              <Route path={ROUTES.campaignDetail.path} element={<CampaignDetail />} />

              {/* PAID SEARCH */}
              <Route path={ROUTES.paidSearch.path} element={<PaidSearchOverview />} />

              {/* OPPORTUNITIES */}
              <Route path={ROUTES.opportunities.path} element={<OpportunitiesOverview />} />
              <Route path={ROUTES.opportunityDetail.path} element={<OpportunityDetail />} />

              {/* PIXELS */}
              <Route path={ROUTES.pixels.path} element={<PixelsOverview />} />
              <Route path={ROUTES.pixelCompare.path} element={<PixelCompare />} />
              <Route path={ROUTES.pixelDetail.path} element={<PixelDetail />} />
              <Route element={<UserRoleRoute role="admin" />}>
                <Route path={ROUTES.pixelCreate.path} element={<PixelCreate />} />
                <Route path={ROUTES.pixelEdit.path} element={<PixelEdit />} />
              </Route>

              {/* AUDIENCES */}
              <Route path={ROUTES.audiences.path} element={<AudiencesList />} />
              <Route path={ROUTES.audienceDetail.path} element={<AudienceDetail />} />
              <Route element={<UserRoleRoute role="admin" />}>
                <Route
                  // TODO: Remove after Sep 30, 2025
                  path={`${ROUTES.instance.path}/audiences/create`}
                  element={<InstanceNavigate to={ROUTES.audienceCSVCreate.path} replace />}
                />
                <Route path={ROUTES.audienceCSVCreate.path} element={<CreateCustomAudience />} />
                <Route path={ROUTES.audienceImport.path} element={<ImportCRMAudience />} />
                <Route
                  path={ROUTES.audienceRuleBasedCreate.path}
                  element={<CreateRuleBasedAudience />}
                />
                <Route path={ROUTES.audienceAIBasedCreate.path} element={<CreateAIAudience />} />
                <Route path={ROUTES.audienceDetailEdit.path} element={<AudienceDetailEdit />} />
              </Route>

              <Route element={<UserRoleRoute role="admin" />}>
                <Route path={ROUTES.configureVendors.path} element={<VendorSpend />} />
                <Route path={ROUTES.configureChannels.path} element={<ChannelMappingRules />} />

                {/* INTEGRATIONS */}
                <Route path={ROUTES.integrations.path} element={<IntegrationsList />} />
                <Route path={ROUTES.integrationDetail.path} element={<IntegrationDetail />} />
                <Route path={ROUTES.verifyIntegration.path} element={<VerifyIntegration />}>
                  <Route path=":provider" element={null} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>,
    ),
  );
};

export default getRouter;
